/**
 * URL privacy for analytics.
 *
 * login-web URLs carry secrets: a magic-link deep link has `?email=&code=`,
 * the Okta redirect lands on `/callback?code=&state=` (or tokens in the hash),
 * and the post-login redirect puts the refresh token in the hash (`#rt=&t=`).
 * PostHog records the page URL on every event and in person properties, so
 * every URL-shaped property is scrubbed before it leaves the browser, and
 * session replay is not started on a page load whose URL carries a secret.
 *
 * A credential can also arrive nested in another param: an app that sends
 * someone back to sign in with its handoff still in its address passes it in
 * `path`. main.ts takes it out of the address before anything reads it, and
 * the scrub removes it from every property should one get through.
 */

/** Query params that must never reach analytics. Matched case-insensitively. */
export const SENSITIVE_QUERY_PARAMS: ReadonlySet<string> = new Set([
  'email',
  'code',
  'state',
  'session_state',
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'rt',
  't',
  'login_hint',
  'iss',
]);

/**
 * Credential parameters that can arrive NESTED inside another parameter's
 * value. An app that sends someone to sign in while its own address still
 * holds login-web's handoff passes `path=%2F%23rt%3D<refresh token>%26t%3D…`;
 * the refresh token lasts until it is revoked.
 */
export const NESTED_CREDENTIAL_PARAMS = [
  'rt',
  't',
  'refresh_token',
  'id_token',
  'access_token',
  'token',
  'code',
] as const;

const NESTED_NAMES = NESTED_CREDENTIAL_PARAMS.join('|');
/** `?rt=…`, `#rt=…`, `&rt=…` or a leading `rt=…`, up to the next parameter, hash or quote. */
const NESTED_RAW = new RegExp(`(^|[?#&])(?:${NESTED_NAMES})=[^&#\\s"'<>]*`, 'gi');
/** The same, percent-encoded (`%23rt%3D…`, `%26t%3D…`, `%3Frt%3D…`). */
const NESTED_ENCODED = new RegExp(
  `(%3F|%23|%26)(?:${NESTED_NAMES})%3D(?:(?!%26|%23)[^&#\\s"'<>])*`,
  'gi',
);

/**
 * Removes credential parameters from anywhere in a string: a query, a hash, or
 * percent-encoded inside another parameter's value. Anything else is returned
 * unchanged.
 */
export function scrubNestedCredentials(value: string): string {
  if (!value.includes('=') && !/%3D/i.test(value)) return value;
  const out = value.replace(NESTED_RAW, '$1').replace(NESTED_ENCODED, '$1');
  if (out === value) return value;
  return out
    .replace(/^&+|([?#])&+/g, '$1')
    .replace(/&{2,}/g, '&')
    .replace(/(%3F|%23)(?:%26)+/gi, '$1')
    .replace(/(?:%26){2,}/gi, '%26')
    .replace(/(?:%3F|%23|%26)+(?=[&#\s"'<>]|$)/gi, '')
    .replace(/[?#&]+(?=[\s"'<>]|$)/g, '')
    .replace(/\?#/g, '#');
}

/**
 * Property names PostHog fills with a URL ($current_url, $referrer,
 * $session_entry_url, $initial_current_url, ...). Matched by name so a URL
 * property added in a future SDK release is still scrubbed.
 */
const URL_PROPERTY = /url|referrer|href/i;

const REDACTED = '[redacted]';

/**
 * Returns `url` with sensitive query params replaced by `[redacted]`, any
 * credential nested in another param's value (`path=%2F%23rt%3D…`) removed,
 * and the hash dropped (it only ever holds tokens or OAuth state here). A
 * value that isn't an absolute URL (e.g. `$pathname`) only loses nested
 * credentials.
 */
export function scrubUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return scrubNestedCredentials(url);
  }
  // Snapshot the entries: the loop rewrites the params it iterates.
  const entries = Array.from(parsed.searchParams.entries());
  for (const [key, value] of entries) {
    if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
      parsed.searchParams.set(key, REDACTED);
    } else {
      const clean = scrubNestedCredentials(value);
      if (clean !== value) parsed.searchParams.set(key, clean);
    }
  }
  parsed.hash = '';
  return parsed.toString();
}

/** True when a query param's value carries a nested credential (`path=/#rt=…`). */
function searchNestsCredential(search: string): boolean {
  for (const value of new URLSearchParams(search).values()) {
    if (scrubNestedCredentials(value) !== value) return true;
  }
  return false;
}

/**
 * Take a credential nested in the `path` param out of login-web's own address,
 * before analytics, replay or anything else reads `location` (main.ts). `app`
 * and the rest of `path` stay: the return URL is rebuilt from `path` and its
 * hash is replaced by the fresh handoff anyway (`LoginService.redirectToApp`),
 * so the person still lands where they were going. Only `path` is touched;
 * login-web's own top-level `code`, `state` and `email` are left for sign-in.
 */
export function removeNestedCredentialsFromAddress(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const path = params.get('path');
    if (path === null) return;
    const clean = scrubNestedCredentials(path);
    if (clean === path) return;
    params.set('path', clean || '/');
    history.replaceState(
      history.state,
      '',
      `${window.location.pathname}?${params.toString()}${window.location.hash}`,
    );
  } catch {
    // history unavailable: scrubUrl and the replay check still cover analytics
  }
}

/** True when this location carries a credential (magic link, OAuth callback, token hash). */
export function locationCarriesSecrets(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
): boolean {
  if (location.pathname.startsWith('/callback')) return true;
  if (location.hash.length > 1) return true;
  const params = new URLSearchParams(location.search);
  for (const key of params.keys()) {
    if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) return true;
  }
  return searchNestsCredential(location.search);
}

type PropertyBag = Record<string, unknown> | undefined | null;

function scrubBag(bag: PropertyBag): void {
  if (!bag) return;
  for (const [key, value] of Object.entries(bag)) {
    if (typeof value !== 'string') continue;
    // URL-named: the full scrub. Anything else: credentials only, in case it holds a URL.
    const clean = URL_PROPERTY.test(key) ? scrubUrl(value) : scrubNestedCredentials(value);
    if (clean !== value) bag[key] = clean;
  }
}

/**
 * Session replay's URL mask (`session_recording.maskCapturedNetworkRequestFn`):
 * the recording's page address (meta `href`, `$pageview`) gets the same scrub.
 */
export function scrubRecordedRequest<T extends { name?: string }>(request: T): T {
  if (typeof request.name === 'string') request.name = scrubUrl(request.name);
  return request;
}

/**
 * `before_send` step: scrubs URL properties on the event and on the person
 * updates (`$set` / `$set_once`) it carries. Mutates and returns the event.
 */
export function scrubEventUrls<
  T extends { properties?: PropertyBag; $set?: PropertyBag; $set_once?: PropertyBag },
>(event: T): T {
  scrubBag(event.properties);
  scrubBag(event.$set);
  scrubBag(event.$set_once);
  const nestedSet = event.properties?.['$set'] as PropertyBag;
  const nestedSetOnce = event.properties?.['$set_once'] as PropertyBag;
  scrubBag(nestedSet);
  scrubBag(nestedSetOnce);
  return event;
}
